import { logger } from '@pkg/shared/logger'
import { asyncDedupe } from '@pkg/shared/utils/async.utils'
import type { Kysely, Transaction } from 'kysely'
import {
  APP_METADATA_LAST_SYNC_TS_KEY,
  APP_METADATA_NODE_ID_KEY,
  type MutateConfig,
  type OutboxMessage,
  type SyncClientDatabase,
} from '../common'
import { createApplyMessage } from '../common/applyMessage'
import { generateNodeId, type NodeId } from '../common/generateNodeId'
import { createMutate } from '../common/mutate'
import { type ClientSyncRequest, type SyncMessage, SyncRequestSchema, type SyncResponse } from '../common/types'

export interface SyncClient<DB extends SyncClientDatabase> {
  sync: (params?: { trx?: Transaction<DB> }) => Promise<void>
  mutate: ReturnType<typeof createMutate<DB>>['mutate']
  remove: ReturnType<typeof createMutate<DB>>['remove']
  restore: ReturnType<typeof createMutate<DB>>['restore']
  beginTransaction: ReturnType<typeof createMutate<DB>>['beginTransaction']
  withTransaction: ReturnType<typeof createMutate<DB>>['withTransaction']
  applyMessage: ReturnType<typeof createApplyMessage>
}

export interface SyncClientConfig<DB extends SyncClientDatabase> {
  db: Kysely<DB>
  userId: MutateConfig<DB>['userId']
  nodeId?: MutateConfig<DB>['nodeId']
  hlc?: MutateConfig<DB>['hlc']
  schema?: MutateConfig<DB>['schema']
  sync: (req: ClientSyncRequest) => Promise<SyncResponse>
  onMessages?: (trx: Transaction<DB>, messages: SyncMessage[]) => void | Promise<void>

  /**
   * Called after a sync response has been applied (including outbox clearing / cursor update).
   *
   * This is intentionally a callback (not an event emitter) to keep the sync client testable and
   * independent from any runtime/leader-election concerns.
   */
  onSynced?: (params: { request: ClientSyncRequest; response: SyncResponse }) => void | Promise<void>
}

export function createSyncClient<DB extends SyncClientDatabase>(config: SyncClientConfig<DB>): SyncClient<DB> {
  const { db, schema } = config

  const applyMessage = createApplyMessage({ db })

  const getState = async (connection: Transaction<SyncClientDatabase> | Kysely<SyncClientDatabase>) => {
    const state = await connection
      .selectFrom('appMetadata')
      .selectAll()
      .where('key', 'in', [APP_METADATA_LAST_SYNC_TS_KEY, APP_METADATA_NODE_ID_KEY])
      .execute()

    let nodeId = state.find(r => r.key === APP_METADATA_NODE_ID_KEY)?.value
    const lastSyncTs = state.find(r => r.key === APP_METADATA_LAST_SYNC_TS_KEY)?.value
    if (!nodeId) {
      nodeId = generateNodeId()
      logger.info({ nodeId }, 'Creating new sync state')
      await connection
        .insertInto('appMetadata')
        .values({
          key: APP_METADATA_NODE_ID_KEY,
          value: nodeId,
        })
        .execute()
    }

    return {
      nodeId: nodeId as NodeId,
      lastSyncTs,
    }
  }

  const { mutate, remove, restore, beginTransaction, withTransaction } = createMutate<DB>({
    db,
    nodeId: trx => getState(trx as never).then(r => r.nodeId),
    userId: config.userId,
    hlc: config.hlc,
    schema,
    onMessages: async (trx, messages) => {
      await applyMessage(messages, trx)
      await config.onMessages?.(trx, messages)
    },
  })

  const executeSync = async (params: { trx?: Transaction<SyncClientDatabase> } = {}) => {
    const startedAt = Date.now()
    const connection = params.trx ?? (db as never as Kysely<SyncClientDatabase>)

    const pending = await connection
      .selectFrom('message')
      .selectAll()
      .orderBy('intentTs')
      .$castTo<OutboxMessage>()
      .execute()

    logger.debug(
      {
        pendingCount: pending.length,
      },
      'Preparing sync request',
    )

    const { nodeId, lastSyncTs } = await getState(connection)

    const syncRequest = SyncRequestSchema.omit({ userId: true }).parse({
      nodeId,
      lastSyncTs,
      clientTime: new Date().toISOString(),
      messages: pending,
    })

    logger.debug(
      {
        nodeId,
        lastSyncTs,
        requestMessageCount: syncRequest.messages.length,
      },
      'Sync started',
    )

    const response = await config.sync(syncRequest)

    logger.debug(
      {
        nodeId,
        request: syncRequest.messages.length,
        response: response.messages?.length,
        lastSyncTs,
        newSyncTs: response.newSyncTs,
        hasMore: response.hasMore,
      },
      'Sync request and response details',
    )

    // 4. Apply server messages and update state
    const execute = async (trx: Transaction<SyncClientDatabase>) => {
      const applyStartedAt = Date.now()

      // Apply messages to projection tables
      await applyMessage(response.messages, trx as never)

      // Clear processed outbox entries
      if (pending.length > 0) {
        await trx
          .deleteFrom('message')
          .where(
            'intentTs',
            'in',
            pending.map(p => p.intentTs),
          )
          .execute()
      }

      logger.debug(
        {
          nodeId,
          newSyncTs: response.newSyncTs,
          appliedMessageCount: response.messages?.length ?? 0,
          clearedOutboxCount: pending.length,
          applyDurationMs: Date.now() - applyStartedAt,
        },
        'Applied messages and cleared outbox',
      )

      // Should normally not happen. This means empty messages
      if (response.newSyncTs) {
        // Update syncState cursor
        await trx
          .insertInto('appMetadata')
          .values({
            key: APP_METADATA_LAST_SYNC_TS_KEY,
            value: response.newSyncTs,
          })
          .onConflict(cb =>
            cb.column('key').doUpdateSet({
              value: response.newSyncTs,
            }),
          )
          .execute()
      }
    }
    if (params.trx) {
      await execute(params.trx as never)
    } else {
      await connection.transaction().execute(execute)
    }

    // Notify callers about applied changes. This is emitted for every sync round
    // (including pagination rounds when `hasMore=true`).
    //
    // NOTE: `pending` is the outbox state of this round, which has been cleared in the transaction above.
    await config.onSynced?.({
      request: syncRequest,
      response,
    })

    // 5. If more messages, sync again
    if (response.hasMore) {
      logger.info(
        {
          nodeId,
        },
        'More messages available (hasMore=true). Rerunning sync',
      )
      await executeSync(params)
    }

    logger.trace(
      {
        nodeId,
        durationMs: Date.now() - startedAt,
      },
      'Sync finished',
    )
  }

  const client = {
    sync: asyncDedupe(executeSync),
    mutate,
    remove,
    restore,
    beginTransaction,
    withTransaction,
    applyMessage,
  } as SyncClient<DB>

  return client as never as SyncClient<DB>
}
